import { useState, useEffect, useCallback } from 'react';
import { Icon, Spinner } from '../ui/Icon.js';
import { apiGet } from '../../lib/api.js';
import { apiDelete } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import { useBrand } from '../../lib/brand-context.js';
import { BrandForm } from './BrandForm.js';
import { purgeBrand } from '../../api/brands.js';
import type { BrandDetail, BrandDetailResponse } from '../../types/index.js';

interface BrandListItem {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  aliases: string[] | null;
  description: string | null;
  tags: Array<{ id: string; name: string; color: string }>;
}

interface BrandListResponse {
  data: BrandListItem[];
  total: number;
}

export function BrandList({ tenantId }: { tenantId: string }) {
  const { addToast } = useToast();
  const { refreshBrands } = useBrand();
  const [brands, setBrands] = useState<BrandListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editBrand, setEditBrand] = useState<BrandDetail | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Spec 129 — destructive "Delete brand & all data" purge flow. Tracks the
  // brand targeted for purge, the name the user must re-type to confirm, and
  // the in-flight state.
  const [purgeTarget, setPurgeTarget] = useState<BrandListItem | null>(null);
  const [purgeConfirmName, setPurgeConfirmName] = useState('');
  const [purging, setPurging] = useState(false);

  const loadBrands = useCallback(async () => {
    try {
      const res = await apiGet<BrandListResponse>(
        `/api/v1/tenants/${tenantId}/brands?limit=100&offset=0`,
      );
      setBrands(res.data);
    } catch {
      addToast('Failed to load brands', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, addToast]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  async function handleEdit(brandId: string) {
    try {
      const res = await apiGet<BrandDetailResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}`,
      );
      setEditBrand(res.data);
      setShowForm(true);
    } catch {
      addToast('Failed to load brand details', 'error');
    }
  }

  async function handleDelete(brandId: string) {
    setDeleting(true);
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/brands/${brandId}`);
      addToast('Brand deleted successfully', 'success');
      setDeleteConfirm(null);
      await loadBrands();
      await refreshBrands();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to delete brand', 'error');
    } finally {
      setDeleting(false);
    }
  }

  function closePurge() {
    setPurgeTarget(null);
    setPurgeConfirmName('');
  }

  async function handlePurge() {
    if (!purgeTarget) return;
    setPurging(true);
    try {
      const result = await purgeBrand(tenantId, purgeTarget.id, purgeConfirmName.trim());
      addToast(
        `Brand purged — ${result.total} record${result.total !== 1 ? 's' : ''} deleted`,
        'success',
      );
      closePurge();
      // Refresh both the Settings list AND the global BrandContext so the
      // purged brand disappears from the selector immediately (spec 129 item 4).
      await loadBrands();
      await refreshBrands();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to purge brand', 'error');
    } finally {
      setPurging(false);
    }
  }

  function handleFormClose() {
    setShowForm(false);
    setEditBrand(null);
  }

  async function handleFormSaved() {
    setShowForm(false);
    setEditBrand(null);
    await loadBrands();
    await refreshBrands();
  }

  const filtered = search.trim()
    ? brands.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()))
    : brands;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <Icon name="bx-search" className="absolute top-2.5 left-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search brands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-gray-300 py-2 pr-3 pl-9 text-sm focus:ring-2 focus:ring-violet-500 focus:outline-none"
          />
        </div>
        <button
          onClick={() => {
            setEditBrand(null);
            setShowForm(true);
          }}
          className="hub-btn hub-btn--primary"
        >
          <Icon name="bx-plus" />
          Add brand
        </button>
      </div>

      {/* Brand list */}
      {filtered.length === 0 ? (
        <div className="hub-plate hub-plate--flat py-12 text-center">
          <p className="text-sm text-gray-500">
            {search ? 'No brands match your search.' : 'No brands yet. Create your first brand.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="hub-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Industry</th>
                <th>Tags</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((brand) => (
                <tr key={brand.id}>
                  <td className="font-medium">{brand.name}</td>
                  <td className="text-[var(--hub-color-ink-muted)]">{brand.domain ?? '-'}</td>
                  <td className="text-[var(--hub-color-ink-muted)]">{brand.industry ?? '-'}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {brand.tags?.map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(brand.id)}
                        className="hub-btn hub-btn--quiet"
                        title="Edit"
                      >
                        <Icon name="bx-pencil" />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Reset all collected data for ${brand.name}? (queries, responses, analyses, scores, playbooks). Brand config and templates are kept.`)) return;
                          try {
                            const { apiFetch } = await import('../../lib/api.js');
                            const res = await apiFetch(`/api/v1/tenants/${tenantId}/brands/${brand.id}/data/reset`, { method: 'POST' });
                            if (!res.ok) throw new Error('Reset failed');
                            const result = await res.json() as { data: { total: number } };
                            addToast(`${brand.name}: ${result.data.total} records deleted`, 'success');
                          } catch (err) {
                            addToast(err instanceof Error ? err.message : 'Reset failed', 'error');
                          }
                        }}
                        className="hub-btn hub-btn--quiet"
                        title="Reset collected data"
                      >
                        <Icon name="bx-undo" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(brand.id)}
                        className="hub-btn hub-btn--quiet"
                        title="Delete brand"
                      >
                        <Icon name="bx-trash" />
                      </button>
                      {/* Spec 129 — destructive purge, visually distinct from
                          the soft-delete trash above. Requires typed name
                          confirmation in the modal it opens. */}
                      <button
                        onClick={() => {
                          setPurgeTarget(brand);
                          setPurgeConfirmName('');
                        }}
                        className="hub-btn hub-btn--quiet"
                        title="Delete brand & all data"
                        data-testid="brand-purge"
                        data-brand-id={brand.id}
                      >
                        <Icon name="bx-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-sm rounded-lg bg-white p-6">
            <h3 className="text-lg font-semibold text-gray-900">Delete brand</h3>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete this brand? This will remove all associated data.
              If the brand has active playbooks, deletion will be blocked.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="hub-btn hub-btn--danger"
              >
                {deleting && <Spinner  />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spec 129 — destructive purge confirmation. Requires the user to type
          the brand's EXACT name before the red "Delete everything" button
          enables. Calls DELETE .../purge with { confirm_name }. */}
      {purgeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div
            className="w-full max-w-md rounded-lg bg-white p-6"
            data-testid="brand-purge-modal"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <Icon name="bx-error" />
              </span>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete brand &amp; all data
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  This permanently deletes <span className="font-semibold">{purgeTarget.name}</span>{' '}
                  and every record that references it — queries, responses,
                  analyses, scores, playbooks, templates, and the brand itself.
                  This <span className="font-semibold">cannot be undone</span>.
                </p>
              </div>
            </div>

            <label className="mt-4 block text-sm font-medium text-gray-700">
              Type{' '}
              <span className="font-mono font-semibold text-gray-900">
                {purgeTarget.name}
              </span>{' '}
              to confirm
            </label>
            <input
              type="text"
              autoFocus
              value={purgeConfirmName}
              onChange={(e) => setPurgeConfirmName(e.target.value)}
              placeholder={purgeTarget.name}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
              data-testid="brand-purge-confirm-input"
            />

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={closePurge}
                disabled={purging}
                className="hub-btn hub-btn--ghost"
                data-testid="brand-purge-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handlePurge}
                disabled={purging || purgeConfirmName.trim() !== purgeTarget.name}
                className="hub-btn hub-btn--danger"
                data-testid="brand-purge-confirm"
              >
                {purging && <Spinner  />}
                Delete everything
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand form modal */}
      {showForm && (
        <BrandForm
          tenantId={tenantId}
          brand={editBrand}
          onClose={handleFormClose}
          onSaved={handleFormSaved}
          onBrandChanged={() => {
            void loadBrands();
            void refreshBrands();
          }}
        />
      )}
    </div>
  );
}
